import { useChat as useVercelChat } from "@ai-sdk/react";
import { createNewChat } from "@Api/createNewChat";
import { gatewayFetch } from "@Auth/session";
import { gatewayUrl } from "@Constants/gatewayUrl";
import { WordPluginChatOrigin } from "@juristic/types/models/prompt";
import { UiDataPartType } from "@juristic/types/stream/ai/data_events";
import { ToolHookEventType } from "@juristic/types/stream/ai/tool_hooks";
import React from "react";
import { findDockedChoice, hasPendingHookCard } from "../components/utils/waitingIndicator";
import { getMessageContext } from "../store/chatComposerStore";
import { useChatSessionStore } from "../store/chatSessionStore";
import { createChatSession } from "./chatSession";
import { createClientToolGate } from "./clientTools/clientToolGate";
import { connectWordSnapshots, syncWordLive, uploadSnapshot } from "./clientTools/wordSnapshots";
import { isMutatingClientTool, runClientTool } from "./clientTools/clientToolRegistry";
import { useWordExecCards } from "./clientTools/wordExec";
import { canUpgradeFromMessage, cardErrorCode, isUsageLimitMessage, useChatError } from "./useChatError";
const currentDocId = ()=>useChatSessionStore.getState().docId ?? "";
function browserStorage() {
    try {
        return globalThis.localStorage;
    } catch  {
        return null;
    }
}
export function textOf(message) {
    return message.parts.map((part)=>part.type === "text" ? part.text : "").join("");
}
function newDocumentsOf(message) {
    return (message?.metadata?.documents ?? []).flatMap((document)=>document.persisted ? [] : [
            {
                link: document.link,
                filename: document.filename,
                subtype: document.extension
            }
        ]);
}
function requestError(message) {
    return {
        id: `http-error-${Date.now()}`,
        source: "http",
        message,
        retryable: true,
        timestamp: Date.now()
    };
}
export function useChat() {
    const chatId = useChatSessionStore((s)=>s.chatId);
    const isNewChat = useChatSessionStore((s)=>s.isNewChat);
    const setIsNewChat = useChatSessionStore((s)=>s.setIsNewChat);
    const [httpError, setHttpError] = React.useState(null);
    const [isLoadingHistory, setIsLoadingHistory] = React.useState(!isNewChat);
    const [{ session, clientTools }] = React.useState(()=>{
        const chatUrl = `${gatewayUrl}/chats/${chatId}`;
        const clientTools = createClientToolGate({
            chatUrl,
            chatId,
            fetch: gatewayFetch,
            storage: browserStorage(),
            run: runClientTool,
            isMutating: isMutatingClientTool,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            answer: (toolCallId, payload)=>created.answerCard(toolCallId, payload),
            onError: (error)=>{
                console.error("[chat] client tool failed:", error);
                setHttpError({
                    ...requestError(cardErrorCode(error, "client-tool-failed")),
                    retryable: false
                });
            }
        });
        const created = createChatSession({
            url: chatUrl,
            chatId,
            fetch: clientTools.fetch,
            body: (outgoing)=>({
                    calledFrom: WordPluginChatOrigin.WordPlugin,
                    docId: currentDocId(),
                    ...getMessageContext(),
                    newDocuments: newDocumentsOf(outgoing)
                }),
            onError: (error)=>console.error("[chat] error:", error),
            beforeSend: ()=>uploadSnapshot(),
            onData: ({ type, data })=>{
                const event = data;
                if (type === UiDataPartType.ToolHook && event.type === ToolHookEventType.ClientToolRequest) {
                    clientTools.handle(data);
                }
            }
        });
        return {
            session: created,
            clientTools
        };
    });
    const { messages, status, error: sdkError } = useVercelChat({
        chat: session.chat
    });
    React.useEffect(()=>{
        const disconnect = connectWordSnapshots((path, payload)=>gatewayFetch(`${gatewayUrl}/chats/${chatId}${path}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    calledFrom: WordPluginChatOrigin.WordPlugin,
                    docId: currentDocId(),
                    ...payload
                })
            }));
        if (!isNewChat) {
            session.load().catch((error)=>error.status === 403 || error.status === 404 ? createNewChat() : setHttpError(requestError("load-failed"))).finally(()=>setIsLoadingHistory(false));
        }
        return ()=>{
            disconnect();
            clientTools.dispose();
            session.dispose();
        };
    }, []);
    const isChatStored = !isNewChat && messages.some((message)=>message.role === "assistant");
    React.useEffect(()=>isChatStored ? syncWordLive() : undefined, [
        isChatStored
    ]);
    const refuseIfLimited = React.useCallback(()=>{
        const { error, lastMessage } = session.chat;
        if (!error || !isUsageLimitMessage(error.message)) return undefined;
        const refused = lastMessage?.role === "user" ? lastMessage : undefined;
        if (refused) session.chat.messages = session.chat.messages.slice(0, -1);
        session.chat.clearError();
        return {
            text: refused ? textOf(refused) : "",
            canUpgrade: canUpgradeFromMessage(error.message)
        };
    }, []);
    const sendMessage = React.useCallback(async (content, requestOptions)=>{
        setHttpError(null);
        if (useChatSessionStore.getState().isNewChat) setIsNewChat(false);
        await session.send(content, requestOptions);
        return refuseIfLimited();
    }, [
        setIsNewChat,
        refuseIfLimited
    ]);
    const retry = React.useCallback(async (requestOptions)=>{
        setHttpError(null);
        const last = session.chat.lastMessage;
        if (last?.role !== "user") {
            await session.resume().catch(()=>setHttpError(requestError("load-failed")));
            return undefined;
        }
        await session.send({
            text: textOf(last),
            metadata: last.metadata,
            messageId: last.id
        }, requestOptions);
        return refuseIfLimited();
    }, [
        refuseIfLimited
    ]);
    const stop = React.useCallback(()=>{
        clientTools.cancel();
        session.stop().catch(()=>setHttpError(requestError("stop-failed")));
    }, []);
    const [toolDecisions, setToolDecisions] = React.useState({});
    const [respondingToolIds, setRespondingToolIds] = React.useState(()=>new Set());
    const respondToToolConfirmation = React.useCallback(async (toolCallId, approved)=>{
        if (toolDecisions[toolCallId] !== undefined) return true;
        setRespondingToolIds((prev)=>new Set(prev).add(toolCallId));
        try {
            const isAccepted = await session.answerCard(toolCallId, {
                approved
            });
            if (isAccepted) setToolDecisions((prev)=>({
                    ...prev,
                    [toolCallId]: approved
                }));
            return isAccepted;
        } catch (err) {
            console.error("[chat] tool confirmation failed:", err);
            setHttpError(requestError(cardErrorCode(err, "tool-approval-failed")));
            return false;
        } finally{
            setRespondingToolIds((prev)=>{
                const next = new Set(prev);
                next.delete(toolCallId);
                return next;
            });
        }
    }, [
        toolDecisions
    ]);
    const [choiceAnswers, setChoiceAnswers] = React.useState({});
    const answerChoice = React.useCallback(async (toolCallId, payload)=>{
        setRespondingToolIds((prev)=>new Set(prev).add(toolCallId));
        try {
            const isAccepted = await session.answerCard(toolCallId, payload);
            if (isAccepted) setChoiceAnswers((prev)=>({
                    ...prev,
                    [toolCallId]: payload
                }));
            return isAccepted;
        } catch (err) {
            console.error("[chat] answer failed:", err);
            const code = cardErrorCode(err, "");
            if (!code) throw err;
            setHttpError(requestError(code));
            return false;
        } finally{
            setRespondingToolIds((prev)=>{
                const next = new Set(prev);
                next.delete(toolCallId);
                return next;
            });
        }
    }, []);
    const dockedChoice = React.useMemo(()=>findDockedChoice(messages, choiceAnswers), [
        messages,
        choiceAnswers
    ]);
    const last = messages[messages.length - 1];
    const chatError = useChatError(httpError, sdkError, status, last);
    const isAgentResponding = status === "submitted" || status === "streaming";
    useWordExecCards((state)=>state.cards);
    const isAwaitingCard = last?.role === "assistant" && hasPendingHookCard(last.parts);
    return {
        messages,
        status,
        isAgentResponding,
        isComposerBusy: isAgentResponding && !isAwaitingCard,
        sendMessage,
        stop,
        respondingToolIds,
        respondToToolConfirmation,
        toolDecisions,
        choiceAnswers,
        answerChoice,
        dockedChoice,
        chatError,
        retry,
        isLoadingHistory
    };
}
